import { c as _c } from "react/compiler-runtime";
import Grid from '@precis-digital/kurama/Grid';
import { styled } from '@precis-digital/kurama/utils';
import { type CSSObject } from '@precis-digital/kurama/theme';
import { lcm } from 'mathjs';
import { CHART_CARD_CARD_LAYOUT } from '../../constants';
import { Children, type ComponentProps, type ReactElement, type PropsWithChildren } from 'react';
interface BreakInRows {
  columns: number;
  breakAt?: number;
}
const breakUpColumns = (columns: number, numChildren: number): BreakInRows => {
  if (columns <= 2) {
    columns = Math.max(columns, 1);
    if (numChildren % columns === 0) {
      return {
        columns
      };
    }
    return {
      columns,
      breakAt: numChildren - numChildren % columns
    };
  }
  if (numChildren <= columns) {
    return {
      columns: numChildren
    };
  }
  const mod = numChildren % columns;
  if (mod === 0) {
    return {
      columns
    };
  }
  const oneLessColumn = columns - 1;
  if (mod === oneLessColumn) {
    return {
      columns,
      breakAt: numChildren - oneLessColumn
    };
  }
  if (numChildren <= oneLessColumn * 2) {
    return breakUpColumns(oneLessColumn, numChildren);
  }
  if ((numChildren - oneLessColumn * 2) % columns === 0) {
    return {
      columns,
      breakAt: numChildren - oneLessColumn * 2
    };
  }
  return breakUpColumns(oneLessColumn, numChildren);
};
const getBreakpoints = (columnMinWidth: number): Array<[number, number]> => [[0, 1], [columnMinWidth, 1], [columnMinWidth * 2 + CHART_CARD_CARD_LAYOUT.gap, 2], [columnMinWidth * 3 + CHART_CARD_CARD_LAYOUT.gap * 2, 3], [columnMinWidth * 4 + CHART_CARD_CARD_LAYOUT.gap * 3, 4], [columnMinWidth * 5 + CHART_CARD_CARD_LAYOUT.gap * 4, 5]];
const getBreakpointTarget = (containerName: string, breakpoint: number, nextBreakpoint: number | undefined): string => {
  const maxWidth = nextBreakpoint == null ? '' : ` and (max-width: ${nextBreakpoint - 1}px)`;
  return `@container ${containerName} (min-width: ${breakpoint}px)${maxWidth}`;
};
const createGridTemplateFixedColumns = (containerName: string, columnMinWidth: number, numChildren: number): CSSObject => {
  const breakpoints = getBreakpoints(columnMinWidth);
  const breaks = breakpoints.map<[number, BreakInRows]>(([breakpoint, columns]) => [breakpoint, breakUpColumns(columns, numChildren)]);
  const breasCSS = breaks.reduce<CSSObject>((acc, [breakpoint, {
    columns
  }], index) => {
    const target = getBreakpointTarget(containerName, breakpoint, breakpoints[index + 1]?.[0]);
    acc[target] = {
      gridTemplateColumns: `repeat(${Math.max(1, columns)}, minmax(${columnMinWidth}px, 1fr))`
    };
    return acc;
  }, {});
  return {
    gridTemplateColumns: `minmax(${columnMinWidth}px, 1fr)`,
    ...breasCSS
  };
};
const createGridTemplateFillColumns = (containerName: string, columnMinWidth: number, numChildren: number): CSSObject => {
  const breakpoints = getBreakpoints(columnMinWidth);
  const breaks = breakpoints.map<[number, BreakInRows]>(([breakpoint, columns]) => [breakpoint, breakUpColumns(columns, numChildren)]);
  const breasCSS = breaks.reduce<CSSObject>((acc, [breakpoint, {
    columns,
    breakAt
  }], index) => {
    const target = getBreakpointTarget(containerName, breakpoint, breakpoints[index + 1]?.[0]);
    if (breakAt == null || columns <= 1) {
      acc[target] = {
        gridTemplateColumns: `repeat(${Math.max(1, columns)}, minmax(0px, 1fr))`
      };
      return acc;
    }
    const lessColumns = columns - 1;
    const gridColumns = lcm(columns, lessColumns);
    acc[target] = {
      gridTemplateColumns: `repeat(${gridColumns}, minmax(0px, 1fr))`,
      '> *': {
        gridColumn: `span ${gridColumns / columns}`
      },
      [`> div:nth-of-type(n + ${breakAt + 1})`]: {
        gridColumn: `span ${gridColumns / lessColumns}`
      }
    };
    return acc;
  }, {});
  return {
    gridAutoFlow: 'row',
    ...breasCSS
  };
};
const StyledCardsInnerContainer = styled(Grid, {
  shouldForwardProp: prop => prop !== 'containerName' && prop !== 'columnMinWidth' && prop !== 'fixedColumns'
})(({
  children,
  containerName,
  columnMinWidth,
  fixedColumns
}: PropsWithChildren<{
  containerName: string;
  columnMinWidth: number;
  fixedColumns?: boolean;
}>) => ({
  display: 'grid',
  gap: CHART_CARD_CARD_LAYOUT.gap,
  width: '100%',
  ...(fixedColumns === true ? createGridTemplateFixedColumns(containerName, columnMinWidth, Children.count(children)) : createGridTemplateFillColumns(containerName, columnMinWidth, Children.count(children)))
}));
const StyledQueryContainer = styled('div', {
  shouldForwardProp: prop => prop !== 'containerName'
})(({
  containerName
}: {
  containerName: string;
}) => ({
  containerType: 'inline-size',
  containerName,
  width: '100%'
}));
const StyledCardsContainer = props => {
  const $ = _c(5);
  let t0;
  if ($[0] !== props) {
    t0 = <StyledCardsInnerContainer {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== props.containerName || $[3] !== t0) {
    t1 = <StyledQueryContainer containerName={props.containerName}>{t0}</StyledQueryContainer>;
    $[2] = props.containerName;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
};
export default StyledCardsContainer;